import * as Icons from '@phosphor-icons/react';
import { clone, isEmpty, isNil } from 'ramda';
import { createContext, type ReactNode, useContext, useEffect, useReducer, useState } from 'react';
import { TAB_TYPES } from '../../../constants/tab';
import { getCustomPageComponents } from '../../../sources/customPageComponents';
import { getPage, savePage } from '../../../sources/page';
import translations from '../../../translations';
import type {
    PageCondition,
    PageElementRequestAPI,
    PageElementResponseAPI,
} from '../../../types/page';
import type { ComponentRegistry, PageEditorState, PreviewMode } from '../../../types/pageEditor';
import type { Tab } from '../../../types/tab';
import type { ValueElementIdReferenceAPI } from '../../../types/value';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import { useTabsDispatch } from '../../tabs/TabsProvider';
import {
    COMPONENT_GROUPS,
    COMPONENT_TYPE,
    COMPONENT_CONFIGURATION_OPTIONS as componentConfigurationOption,
} from '../constants';
import { componentRegistry } from '../registry';
import { filterComponentsToRender, initPage } from '../utils';
import PageEditorReducer, { type PageAction } from './PageEditorReducer';

interface PageEditorProviderContext {
    updatePage: (page: PageElementRequestAPI) => void;
    setPageTab: (tab: Tab) => void;
    state: PageEditorState;
    onSave: (
        updatedPage: PageElementRequestAPI,
        successMessage?: string,
    ) => Promise<PageElementResponseAPI | undefined>;
    setModifiedPageConditions: (isModified: boolean) => void;
    setPreviewMode: (mode: PreviewMode | null) => void;
    setPageName: (name: string | null) => void;
    setShowPageConditions: (show: boolean) => void;
    setShowMetadataEditor: (show: boolean) => void;
    setPageConditions: (pageConditions: PageCondition[]) => void;
    onLoad: () => void;
    selectComponentSuggestValue: (elementReference: ValueElementIdReferenceAPI | null) => void;
    setStopComponentSuggest: () => void;
    getValidComponents: (contentType: string) => ComponentRegistry[];
    getComponent: (key: string | null) => ComponentRegistry;
    loadCustomPageComponents: () => void;
    clonePage: (page: PageElementRequestAPI) => void;
    toggleUnsavedChanges: (toggle: boolean) => void;
    isLoading: boolean;
    container: HTMLElement | null;
    componentRegistryList: ComponentRegistry[];
}

const Context = createContext<PageEditorProviderContext | undefined>(undefined);

interface PageEditorProviderProps {
    showConfirmation: boolean;
    tabKey: string;
    initialState?: PageEditorState | null | undefined;
    containerId: string;
    children: ReactNode;
}

const defaultState = {
    preview: null,
    page: initPage(),
    showPageConditions: false,
    showMetadataEditor: false,

    // Used to make sure the user doesn't abandon a modified or incomplete page condition without
    // being warned about it. This is not the same as unsaved changes (showConfirmation).
    // A condition will toggle an unsaved change only after it has been applied which means it was
    // saved from the page condition context into the main page builder context's page metadata.
    modifiedPageCondition: false,
    showPageLoader: true,
    showCustomPageComponentsLoader: false,
    tab: null,
    user: null,
    showComponentSuggest: false,
    componentSuggestValue: null,
};

const PageEditorProvider = ({
    showConfirmation,
    tabKey,
    initialState,
    containerId,
    children,
}: PageEditorProviderProps) => {
    const [container, setContainer] = useState<HTMLElement | null>(null);
    const dispatchTabs = useTabsDispatch();

    const dispatchNotification = useNotificationDispatch();

    useEffect(() => {
        const element = document.getElementById(containerId);
        setContainer(element);
    });

    const combinedInitialState =
        isNil(initialState) || isEmpty(initialState)
            ? defaultState
            : {
                  ...defaultState,
                  ...initialState,
              };

    const [state, dispatch] = useReducer(PageEditorReducer, combinedInitialState);

    const [combinedComponentRegistry, setCombinedComponentRegistry] = useState(componentRegistry);

    const componentRegistryList = filterComponentsToRender(combinedComponentRegistry);

    const update = (action: PageAction) => dispatch(action);

    const updatePage = (page: PageElementRequestAPI = initPage()) =>
        update({ type: 'set_page', payload: page });

    const setModifiedPageConditions = (isModified: boolean) =>
        update({ type: 'set_modified_page_condition', payload: isModified });

    const setPageTab = (tab: Tab) => update({ type: 'set_page_tab', payload: tab });

    const setPreviewMode = (mode: PreviewMode | null) =>
        update({ type: 'set_page_preview', payload: mode });

    const setIsLoading = (isLoading: boolean) =>
        update({ type: 'set_show_page_loader', payload: isLoading });

    const setCustomComponentsIsLoading = (isLoading: boolean) =>
        update({ type: 'set_show_custom_component_loader', payload: isLoading });

    const setShowPageConditions = (show: boolean) =>
        update({ type: 'set_show_page_conditions_panel', payload: show });

    const setShowMetadataEditor = (show: boolean) =>
        update({ type: 'set_show_metadata_editor', payload: show });

    const setPageConditions = (pageConditions: PageCondition[]) => {
        update({ type: 'set_page_conditions', payload: pageConditions });
        toggleUnsavedChanges(true);
    };

    const setStartComponentSuggest = (payload: ValueElementIdReferenceAPI | null) =>
        update({ type: 'set_start_component_suggest', payload });

    const setStopComponentSuggest = () => update({ type: 'set_stop_component_suggest' });

    const selectComponentSuggestValue = (elementReference: ValueElementIdReferenceAPI | null) => {
        setStartComponentSuggest(elementReference);
    };

    const clonePage = (page: PageElementRequestAPI) => {
        onSave({ ...page, id: null }, translations.PAGE_clone_success);
    };

    const setPageName = (name: string | null) => {
        updatePage({
            ...state.page,
            developerName: name,
        });

        dispatchTabs({
            type: 'SET_TITLE',
            payload: {
                tabKey,
                title: name ?? '',
            },
        });

        dispatchTabs({
            type: 'SET_CLOSE_CONFIRMATION_REQUIRED',
            payload: {
                tabKey,
                isRequired: true,
            },
        });
    };

    const toggleUnsavedChanges = (toggle: boolean) => {
        const updatedUnsavedChanges = isNil(toggle) ? showConfirmation : toggle;
        if (showConfirmation !== updatedUnsavedChanges) {
            dispatchTabs({
                type: 'SET_CLOSE_CONFIRMATION_REQUIRED',
                payload: {
                    tabKey,
                    isRequired: updatedUnsavedChanges,
                },
            });
        }
    };

    const onLoad = async () => {
        try {
            setIsLoading(true);

            const loadedPage = await getPage(state?.tab?.elementId || '');

            if (loadedPage) {
                dispatchTabs({
                    type: 'SET_TITLE',
                    payload: {
                        tabKey,
                        title: loadedPage.developerName,
                    },
                });
                updatePage(loadedPage as PageElementRequestAPI);
                toggleUnsavedChanges(false);
            }
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error: `${translations.PAGE_BUILDER_unable_to_load_page}: ${(
                        error as Error
                    ).toString()}`,
                },
            });
        } finally {
            setIsLoading(false);
        }
    };

    const onSave = async (
        updatedPage: PageElementRequestAPI,
        successMessage?: string,
    ): Promise<PageElementResponseAPI | undefined> => {
        try {
            const savedPage = await savePage(updatedPage);

            dispatchTabs({
                type: 'SET_CLOSE_CONFIRMATION_REQUIRED',
                payload: {
                    tabKey,
                    isRequired: false,
                },
            });

            dispatchNotification({
                type: 'notifySuccess',
                payload: {
                    message: successMessage ?? translations.PAGE_save_success,
                },
            });
            return savedPage;
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error: `${translations.PAGE_BUILDER_unable_to_save_page}: ${
                        (error as Error).message
                    }`,
                },
            });
        }

        return undefined;
    };

    const getValidComponents = (contentType: string): ComponentRegistry[] =>
        componentRegistryList.filter((component) =>
            component?.componentSuggestContentTypeFilter?.includes(contentType),
        );

    const getComponent = (key: string | null) => {
        if (key !== null) {
            return (
                combinedComponentRegistry[key?.toUpperCase()] ??
                combinedComponentRegistry[COMPONENT_TYPE['UNKNOWN']]
            );
        }

        return combinedComponentRegistry[COMPONENT_TYPE['UNKNOWN']];
    };

    const loadCustomPageComponents = async () => {
        setCustomComponentsIsLoading(true);
        const customComponents: { [x: string]: ComponentRegistry } = {};
        try {
            const components = await getCustomPageComponents({
                page: 1,
                pageSize: -1,
                orderBy: 'developerName',
                orderDirection: 'ASC',
            });

            components.items.forEach((component) => {
                const mockComponent = clone(componentRegistry[COMPONENT_TYPE['UNKNOWN']]);

                mockComponent.group = COMPONENT_GROUPS['CUSTOM'];
                mockComponent.type = component?.key?.toUpperCase();
                mockComponent.componentType = component.componentType;

                const iconKey =
                    // Remove these keys as we don't use them and their inclusion changes type inference
                    (
                        Object.keys(Icons).filter((key) => {
                            return ['IconContext', 'IconBase', 'SSR'].includes(key) === false;
                        }) as (keyof Omit<typeof Icons, 'IconContext' | 'IconBase' | 'SSR'>)[]
                    ).find((key) => {
                        return key === component.icon;
                    });

                // Key should never be undefined
                // biome-ignore lint/performance/noDynamicNamespaceImportAccess: Requires dedicated refactor to work without dynamically accessing namespace imports
                const ComponentIcon = Icons[iconKey!];

                if (ComponentIcon) {
                    mockComponent.ui.icon = <ComponentIcon />;
                }
                mockComponent.ui.caption = component.developerName!;
                mockComponent.ui.description = component.developerSummary!;
                mockComponent.configuration = [
                    ...(component.configurationEditors || []),
                    componentConfigurationOption['NAME'],
                ];
                mockComponent.renderFn = () => (
                    <span className="full-width component-preview">{component.developerName}</span>
                );
                if (
                    component.designTimeRenderType &&
                    componentRegistry[component.designTimeRenderType.toUpperCase()]
                ) {
                    mockComponent.renderFn =
                        componentRegistry[component.designTimeRenderType.toUpperCase()].renderFn;
                }
                if (component.designTimeImageURL) {
                    mockComponent.renderFn = () => (
                        <img
                            className="full-width component-preview custom-page-component-preview component-preview-image"
                            src={component.designTimeImageURL}
                            alt={mockComponent.ui.caption}
                        />
                    );
                }
                if (component.attributes && Object.entries(component.attributes).length > 0) {
                    mockComponent.attributes = Object.entries(component.attributes).map(
                        ([key, value]) => ({
                            label: value,
                            value: key,
                        }),
                    );
                }

                customComponents[mockComponent.type] = mockComponent;
            });
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        } finally {
            setCombinedComponentRegistry({ ...componentRegistry, ...customComponents });
            setCustomComponentsIsLoading(false);
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: Treat warnings as errors, fix later
    useEffect(() => {
        if (!isNil(state.tab)) {
            const { id: currentPageId } = state.page;
            const { elementId: tabPageId, type: tabType } = state.tab;

            const isExistingPageFirstRender =
                tabPageId && tabPageId !== currentPageId && tabType !== TAB_TYPES.newPage;

            if (isExistingPageFirstRender) {
                onLoad();
            }

            const isNewPageFirstRender = tabType === TAB_TYPES.newPage && state.showPageLoader;

            if (isNewPageFirstRender) {
                setIsLoading(false);
            }

            if (isNewPageFirstRender || isExistingPageFirstRender) {
                loadCustomPageComponents();
            }
        }
    }, [state.tab]);

    const contextValue = {
        state,
        setModifiedPageConditions,
        updatePage,
        setPageTab,
        setPreviewMode,
        setPageName,
        setShowPageConditions,
        setShowMetadataEditor,
        setPageConditions,
        onLoad,
        onSave,
        selectComponentSuggestValue,
        setStopComponentSuggest,
        getValidComponents,
        getComponent,
        loadCustomPageComponents,
        clonePage,
        toggleUnsavedChanges,
        isLoading: state.showPageLoader || state.showCustomPageComponentsLoader,
        container,
        componentRegistryList,
    };

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

const usePageEditor = (): PageEditorProviderContext => {
    const context = useContext(Context);
    if (context === undefined) {
        throw new Error('usePageEditor must be used within a PageEditorProvider');
    }
    return context;
};

export { PageEditorProvider, usePageEditor };
